import { AvailabilityRequest } from 'gql/autogen/AvailabilityRequest';
import { RootState } from 'store/store.types';
import { mapActivityForDetail } from 'gql/service/graph/activityService';
import { ActivityGqlOperation, AvailabilityRequestProps, GqlLogTags } from 'store/api/api.types';
import { InternalContext } from 'gql/autogen/InternalContext';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { getGeoLogTags } from 'core/service/measurements/measurementUtils';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import { AvailabilityRequestParameters } from 'gql/autogen/AvailabilityRequestParameters';
import { ExperimentId } from 'core/type/ExperimentId';
import { isB } from 'core/window/bootstrapConfig';
import { Pax } from 'gql/autogen/Pax';

const build = (state: RootState, props: AvailabilityRequestProps, context: InternalContext): AvailabilityRequest => {
    const { activityToken, excludePassengers, enableOfferFilter, enableOffersGrouping } = props;
    const offerId = state.availability.selectedOffer?.offer?.offerId;
    const offerGroupId = state.availability.selectedOffer?.offer?.offerGroupId || 0;
    const possibleOfferOptions = state.availability.offerOptionsState?.[offerGroupId]?.possibleOfferOptions;
    const validOfferOptions = possibleOfferOptions && possibleOfferOptions.length === 1 && possibleOfferOptions[0].isAvailable ? possibleOfferOptions[0] : undefined;
    const loyaltySearchType = state.bootstrap.loyaltySearchType === LoyaltySearchType.UNKNOWN ? undefined : state.bootstrap.loyaltySearchType;
    const config = state.bootstrap.config;
    const isDetailRefactorEnabled = isB(ExperimentId.ACT_4944, config.experiments);

    let offerOptionToken = activityToken;
    let pax: Pax[] = isDetailRefactorEnabled ? [] : state.availability.passengers;
    let travelDate = getDateInYmdFormat(state.availability.selectedDate);

    // TODO: Remove these else conditions and the definition above when integrate ACT-4944
    if (isDetailRefactorEnabled) {
        offerOptionToken = validOfferOptions?.offerOptionToken || activityToken;
        pax = state.availability.offerOptionsState?.[offerGroupId]?.passengers || [];
        travelDate = getDateInYmdFormat(state.availability.offerOptionsState?.[offerGroupId]?.selectedDate || state.availability.selectedDate);
    } else if (enableOffersGrouping && state.availability.offerOptionsState) {
        offerOptionToken = validOfferOptions?.offerOptionToken || activityToken;
        pax = state.availability.offerOptionsState?.[offerGroupId]?.passengers || state.availability.passengers;
        travelDate = getDateInYmdFormat(state.availability.offerOptionsState?.[offerGroupId]?.selectedDate || state.availability.selectedDate);
    } else if (offerId) {
        offerOptionToken = state.availability.selectedOffer?.offer?.enrichedOfferOption?.[0].offerOptionToken || activityToken;
        pax = state.availability.offerOptionsState[offerId]?.passengers || state.availability.passengers;
        travelDate = getDateInYmdFormat(state.availability.offerOptionsState[offerId]?.selectedDate || state.availability.selectedDate);
    }

    const passengers = (excludePassengers ? [] : pax) || [];

    const availabilityRequest: AvailabilityRequestParameters = {
        activityToken: offerOptionToken,
        travelDate: { value: travelDate },
        passengerSummary: {
            passengers,
            childAges: [],
        },
        featureControl: {
            enableOfferFilter: enableOfferFilter || false,
            enableOfferTimeFilter: false,
            enableOffersGrouping: enableOffersGrouping || false,
        },
        loyaltySearchType,
    };

    return {
        context,
        availabilityRequest,
    };
};

export const buildLogTags = (state: RootState, variables: Record<string, unknown>): GqlLogTags => {
    const request = (variables as unknown as { AvailabilityRequest: AvailabilityRequest }).AvailabilityRequest.availabilityRequest;
    const activity = mapActivityForDetail(state.details);
    const geoTags = getGeoLogTags(state.bootstrap.config);
    return {
        ...geoTags,
        operation: ActivityGqlOperation.availability,
        supplierId: String(activity.masterSupplierId || 0),
        activityToken: request.activityToken,
        // We log the selected date with name 'to_date'. This key is in snake case for consistency.
        to_date: request.travelDate?.value || '',
    };
};

export default build;
